import React from "react";
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Button,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import styled from "@emotion/styled";
import AppDatePicker from "../AppDatePicker";

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const PaymentItem = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
}));

const PaymentFields = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    flex: 1,
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
    },
}));

const EmptyPaymentsMessage = styled(Typography)({
    fontStyle: 'italic',
});

const BudgetRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
    },
}));

const HelperText = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

const StepBilling = ({ form, onChange, payments, setPayments, templateType }) => {
    // Tasks are meant to bill under their parent; changing that is deliberately not offered here.
    const showBilling = templateType !== "task";
    const showPayments = showBilling && form.billingType === "Fixed Price";

    const handleAddPayment = () => {
        const maxPaymentNumber = payments.length > 0 ? Math.max(...payments.map((p) => p.paymentNumber || 0)) : 0;
        setPayments([...payments, { paymentNumber: maxPaymentNumber + 1, value: "", date: "" }]);
    };

    const handleRemovePayment = (index) => {
        setPayments(payments.filter((_, i) => i !== index));
    };

    const handlePaymentChange = (index, field, value) => {
        const updated = [...payments];
        updated[index] = { ...updated[index], [field]: value };
        setPayments(updated);
    };

    return (
        <>
            {showBilling && (
                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Billing Type</InputLabel>
                        <Select name="billingType" value={form.billingType} onChange={onChange} label="Billing Type">
                            <MenuItem value="Parent">Parent</MenuItem>
                            <MenuItem value="Retainer">Retainer</MenuItem>
                            <MenuItem value="Hourly">Hourly</MenuItem>
                            <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="Non Billable">Non Billable</MenuItem>
                        </Select>
                    </FormControl>
                </FormField>
            )}

            <FormField>
                <Typography variant="subtitle1" gutterBottom>Budget</Typography>
                <HelperText variant="body2" color="text.secondary">
                    Hours carried onto every {templateType} created from this template. Leave blank to create it with no budget.
                </HelperText>
                <BudgetRow>
                    <TextField
                        fullWidth
                        label="Budgeted Time"
                        name="budgeted"
                        type="number"
                        value={form.budgeted}
                        onChange={onChange}
                        inputProps={{ min: 0, step: 0.25 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">hrs</InputAdornment> }}
                    />
                    <TextField
                        fullWidth
                        label="Quoted Time"
                        name="quoted"
                        type="number"
                        value={form.quoted}
                        onChange={onChange}
                        inputProps={{ min: 0, step: 0.25 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">hrs</InputAdornment> }}
                    />
                </BudgetRow>
            </FormField>

            {showPayments && (
                <FormField>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                        <Typography variant="subtitle1">Payment Schedule</Typography>
                        <Button startIcon={<Add />} onClick={handleAddPayment} size="small" variant="outlined">
                            Add Payment
                        </Button>
                    </Box>
                    {payments.length > 0 ? (
                        payments.map((payment, index) => (
                            <PaymentItem key={index}>
                                <PaymentFields>
                                    <TextField
                                        fullWidth
                                        label="Payment Value"
                                        type="number"
                                        value={payment.value || ""}
                                        onChange={(e) => handlePaymentChange(index, "value", e.target.value)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        }}
                                        inputProps={{ step: 0.01, min: 0.01 }}
                                        size="small"
                                    />
                                    <AppDatePicker
                                        label="Due Date"
                                        value={payment.date || ""}
                                        onChange={(v) => handlePaymentChange(index, "date", v)}
                                        size="small"
                                    />
                                </PaymentFields>
                                <IconButton onClick={() => handleRemovePayment(index)} color="error" size="small">
                                    <Delete />
                                </IconButton>
                            </PaymentItem>
                        ))
                    ) : (
                        <EmptyPaymentsMessage variant="body2" color="text.secondary">
                            No payments added. Click "Add Payment" to set up payment schedule.
                        </EmptyPaymentsMessage>
                    )}
                </FormField>
            )}
        </>
    );
};

export default StepBilling;
